import moment from 'moment-timezone';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { formatTimezone } from 'src/app/Orders/order.helpers';
import CardCmp from 'src/components/CardCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { appearance as appearanceSelector } from 'src/redux/basic.slice';
import { orderDetailsLoading } from 'src/redux/orders.slice';
import { getDateWithSuffixFormat, getFormattedDateIgnoringTime } from 'src/utils/CommonFunctions';

const WINDOW_TYPE = {
    OPEN: 'open',
    READY_TIME: 'ready_time',
    APPOINTMENT: 'appointment',
    DELIVERY_TIME: 'delivery_time',
} as const;

type WindowType = (typeof WINDOW_TYPE)[keyof typeof WINDOW_TYPE];

const trimSeconds = (time: string | null): string => {
    if (!time) return '';

    return time.length >= 5 ? time.substring(0, 5) : time;
};

// Converts stored wall-clock HH:MM(:SS) from source timezone → user's local timezone.
// Falls back to the trimmed source time when conversion is not possible or unnecessary.
const convertTimeToUserTz = (date: string | null, time: string | null, sourceTimezone: string | null, userTimezone: string | null): string => {
    const trimmed = trimSeconds(time);
    if (!date || !time || !sourceTimezone || !userTimezone || sourceTimezone === userTimezone) return trimmed;
    const m = moment.tz(`${moment.utc(date).format('YYYY-MM-DD')} ${time}`, 'YYYY-MM-DD HH:mm:ss', sourceTimezone).tz(userTimezone);

    return m.isValid() ? m.format('HH:mm') : trimmed;
};

// Below `smd` the Time / Local Time / Timezone columns become label-left / value-right rows
// (same as the mobile Order info details card).
const ROW_ITEM = 'space-y-1 w-[calc(33.33%-3px)] max-smd:w-full max-smd:flex max-smd:items-center max-smd:justify-between max-smd:gap-x-4 max-smd:space-y-0 max-smd:min-w-0';
const ROW_LABEL = 'text-gray500 text-xs leading-[18px] font-normal max-smd:text-grayLight600 max-smd:flex-none';
const ROW_VALUE = 'text-grayLight900 truncate text-sm font-medium max-smd:text-xs max-smd:leading-[18px] max-smd:text-grayLight600 max-smd:text-right max-smd:min-w-0';

const getTimeLabel = (windowType: WindowType, t: (key: string) => string): string => {
    switch (windowType) {
        case WINDOW_TYPE.READY_TIME:
            return t('Orders.Ready_Time');
        case WINDOW_TYPE.DELIVERY_TIME:
            return t('Orders.Delivery_Time');
        case WINDOW_TYPE.APPOINTMENT:
            return t('Orders.Appointment');
        default:
            return t('Common.Time');
    }
};

interface ITimeAndSchedulingProps {
    title: string;
    data: {
        startDate: string;
        endDate: string;
        startTime: string;
        endTime: string;
        timezone: string;
        windowType: WindowType;
    };
}

const TimeAndScheduling = ({ title, data }: ITimeAndSchedulingProps) => {
    const { t } = useTranslation();
    const loading = useSelector(orderDetailsLoading);
    const userTimezone = useSelector(appearanceSelector)?.timezone || 'UTC';
    const timezoneRef = useRef<HTMLParagraphElement>(null);

    const { startDate, endDate, startTime, endTime, timezone, windowType } = data;

    const hasTimeWindow = !!(startTime && endTime);
    const startTimeFmt = trimSeconds(startTime);
    const endTimeFmt = trimSeconds(endTime);

    // Shipper wall-clock time (stored timezone)
    const timeRange = hasTimeWindow && startTimeFmt && endTimeFmt ? `${startTimeFmt} - ${endTimeFmt}` : startTimeFmt || '-';

    // Local Time — same moment converted to the viewing user's timezone
    const startLocal = convertTimeToUserTz(startDate, startTime, timezone, userTimezone);
    const endLocal = convertTimeToUserTz(endDate || startDate, endTime, timezone, userTimezone);
    const timeRangeLocal = hasTimeWindow && startLocal && endLocal ? `${startLocal} - ${endLocal}` : startLocal || timeRange;

    // Header subtext: "between HH:MM to HH:MM" | "at HH:MM" | empty when no time
    const headerTimeText = (): string => {
        if (!startTimeFmt) return '';
        if (hasTimeWindow && endTimeFmt) return `${t('Common.between')} ${startTimeFmt} ${t('Common.to')} ${endTimeFmt}`;

        return `${t('Common.at')} ${startTimeFmt}`;
    };

    return (
        <CardCmp headerTitle={title} headerParentClassName="!rounded-t-lg" cardClassName=" w-full lg:w-[calc(50%_-_8px)] overflow-hidden" childrenClassName="!p-0">
            <>
                <div className="p-3 border-b border-utilityGray200 bg-gray25 flex items-center gap-x-2.5">
                    <div
                        className={`rounded border border-utilityGray200 min-w-[38px] min-h-[38px] flex flex-col items-center justify-center p-1 text-primary700 text-[10px] leading-[14px] font-semibold ${
                            loading ? 'custom-loading' : ''
                        }`}
                    >
                        {loading ? (
                            'Lorem'
                        ) : startDate ? (
                            <>
                                <p>{moment.utc(startDate).format('MMM').toUpperCase()}</p>
                                <p>{moment.utc(startDate).format('DD')}</p>
                            </>
                        ) : (
                            <p>{t('Common.N/A')}</p>
                        )}
                    </div>
                    <div className="flex-1">
                        <h6
                            className={`text-grayLight900 text-sm font-medium ${loading ? 'custom-loading' : ''}`}
                            dangerouslySetInnerHTML={{
                                __html: loading
                                    ? 'lorem ipsum dolor'
                                    : startDate
                                    ? endDate && endDate !== startDate
                                        ? `${getDateWithSuffixFormat(getFormattedDateIgnoringTime(startDate, 'MMM Do, YYYY'))} - ${getDateWithSuffixFormat(
                                              getFormattedDateIgnoringTime(endDate, 'MMM Do, YYYY')
                                          )}`
                                        : getDateWithSuffixFormat(getFormattedDateIgnoringTime(startDate, 'MMM Do, YYYY'))
                                    : '-',
                            }}
                        />
                        <p className={`text-grayLight600 text-xs font-normal mt-px ${loading ? 'custom-loading' : ''}`}>
                            {loading ? 'lorem ipsum dolor' : <span className="font-medium">{startDate ? headerTimeText() || '-' : '-'}</span>}
                        </p>
                    </div>
                </div>
                <div className="p-3 w-full flex items-center justify-between gap-x-1.5 max-smd:flex-col max-smd:items-stretch max-smd:gap-2">
                    <div className={ROW_ITEM}>
                        <p className={ROW_LABEL}>{getTimeLabel(windowType, t)}</p>
                        <p className={`${ROW_VALUE} ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum dolor' : timeRange}</p>
                    </div>
                    <div className={ROW_ITEM}>
                        <p className={ROW_LABEL}>{t('Common.Local_Time')}</p>
                        <p className={`${ROW_VALUE} ${loading ? 'custom-loading' : ''}`}>{loading ? 'lorem ipsum dolor' : timeRangeLocal}</p>
                    </div>
                    <div className={ROW_ITEM}>
                        <p className={ROW_LABEL}>{t('Common.Timezone')}</p>
                        <TruncateTextCmp message={loading ? '' : timezone || '-'} refName={timezoneRef} truncateTextParentClassName="max-smd:min-w-0">
                            <p className={`${ROW_VALUE} ${loading ? 'custom-loading' : ''}`} ref={timezoneRef}>
                                {loading ? 'lorem ipsum dolor' : timezone ? formatTimezone(timezone) : '-'}
                            </p>
                        </TruncateTextCmp>
                    </div>
                </div>
            </>
        </CardCmp>
    );
};

export default TimeAndScheduling;
